import { makeAutoObservable } from 'mobx'
import { toastStore } from 'shared/ui'
import { logger } from 'shared/lib'
import type { Inbox } from 'entities/Inbox/model/Inbox'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { CallApi } from 'entities/Call'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { type ISmartCallFlowGreetingAudioStepBlockArgs } from 'entities/SmartCall/api/types'
import { RinglessMessageFieldStore } from 'widgets/MessageFormFields/RinglessMessageField'
import { RenameModalStore } from 'widgets/RenameModal'
import { MediaLibraryVariant } from 'widgets/MediaLibrary/types/types'

export class GreetingAudioBlockStore {
  ringlessMessageFieldStore: RinglessMessageFieldStore
  errorMessage = 'Please select an audio'
  constructor(
    private _entities: IEntitiesStore,
    readonly parent: { inbox: Inbox | null },
    args?: ISmartCallFlowGreetingAudioStepBlockArgs
  ) {
    this.ringlessMessageFieldStore = new RinglessMessageFieldStore(this._entities, {
      ttsGroup: 'voicemail',
      withAudioDurationValidation: true,
      clearParentError: () => {
        this.setError('')
      },
      onAddAudio: async (file: File, onAddAttachment) => {
        if (this.parent.inbox?.id) {
          try {
            const attachment = await this._uploadGreetingFile(file, this.parent.inbox.id)
            onAddAttachment(attachment)
          } catch (e) {
            logger.error(e)
          }
        }
      },
      onRenameAudio: (attachment) => this._handleRenameGreetingAudio(attachment),
      mediaLibraryConfiguration: {
        onRenameAudio: (attachment) => this._handleRenameGreetingAudio(attachment),
        mediaLibraryVariant: MediaLibraryVariant.AudioSelect,
        onDeleteAttachments: async (ids) => {
          const numericIds = ids.map(Number)
          await Promise.all(numericIds.map((id) => CallApi.deleteVoicemailGreeting(id)))
          this._entities.organizationStore.setVoicemailGreetingsAttachments(
            this._entities.organizationStore.voicemailGreetingsAttachments.filter(
              (item) => !numericIds.includes(Number(item.id))
            )
          )
        },
        onAddFile: async (file: File) => {
          if (!this.parent.inbox?.id) return null
          return this._uploadGreetingFile(file, this.parent.inbox.id)
        },
        fetchAttachmentList: async (term = '') => {
          try {
            const { data } = await CallApi.getVoicemailGreetings()
            const attachments = data.map(
              (item) =>
                new Attachment({ responseVoicemailGreetings: item, isGenerateExtensionName: true })
            )
            this._entities.organizationStore.setVoicemailGreetingsAttachments(attachments)
            return attachments.filter((item) =>
              item.name?.toLowerCase().includes(term.toLowerCase())
            )
          } catch (e) {
            logger.error(e)
            return this._entities.organizationStore.voicemailGreetingsAttachments.filter((item) =>
              item.name?.toLowerCase().includes(term.toLowerCase())
            )
          }
        },
      },
    })
    if (args?.audio_url) {
      const attachment = this._entities.organizationStore.voicemailGreetingsAttachments.find(
        (item) => item.source === args.audio_url
      )
      if (attachment) {
        this.ringlessMessageFieldStore.onAddAttachmentAudio(attachment)
      } else {
        this.ringlessMessageFieldStore.init({
          url: args.audio_url,
        })
      }
    }
    makeAutoObservable(this)
  }

  get args(): ISmartCallFlowGreetingAudioStepBlockArgs {
    const attachment = this.ringlessMessageFieldStore.attachment
    return {
      audio_url: attachment?.source || '',
    }
  }

  validate = () => {
    const isValid = !!this.args?.audio_url
    if (!isValid) {
      this.setError(this.errorMessage)
    } else {
      this.setError('')
    }

    return isValid
  }

  get error() {
    return this.ringlessMessageFieldStore.error
  }
  setError = (value: string) => {
    this.ringlessMessageFieldStore.setError(value)
  }

  private _uploadGreetingFile = async (file: File, inboxId: number): Promise<Attachment> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('name', file.name)
    formData.append('type', 'open_hours')
    formData.append('is_make_active', '0')
    const { data } = await CallApi.uploadVoicemailGreeting(formData, inboxId)
    const attachment = new Attachment({ responseVoicemailGreetings: data })
    this._entities.organizationStore.addVoicemailGreetingsAttachments(attachment)
    return attachment
  }

  private _resolveGreetingAttachment = async (
    attachment: Attachment
  ): Promise<Attachment | null> => {
    if (typeof attachment.id === 'number') return attachment
    if (!attachment.source) return null

    try {
      const { data } = await CallApi.getVoicemailGreetings()
      const attachments = data.map((item) => new Attachment({ responseVoicemailGreetings: item }))
      this._entities.organizationStore.setVoicemailGreetingsAttachments(attachments)
      const match = attachments.find((item) => item.source === attachment.source)
      if (match && typeof match.id === 'number') {
        this.ringlessMessageFieldStore.onAddAttachmentAudio(match)
        return match
      }
      return null
    } catch (e) {
      logger.error(e)
      return null
    }
  }

  private _handleRenameGreetingAudio = async (attachment: Attachment) => {
    const resolved = await this._resolveGreetingAttachment(attachment)
    if (!resolved || typeof resolved.id !== 'number') {
      toastStore.add({
        type: 'error',
        title: 'Unable to rename this audio',
      })
      return
    }
    const attachmentId = resolved.id

    new RenameModalStore({
      title: 'Rename',
      name: resolved.name || '',
      withPostfix: true,
      element: 'Greeting',
      isEditExtensionDisabled: true,
      isRenamedToast: true,
      onSave: async (name: string) => {
        await CallApi.updateVoicemailGreetingName(attachmentId, { name })
        attachment.setName(name)
      },
    })
  }
}
